type TagPageShellData = {
  type: 'artwork' | 'model'
  displayName: string
  title: string
  description: string
  image?: string
  canonicalUrl: string
  heroTitle: string
  heroDescription: string
}

function buildTagJsonLd(shell: TagPageShellData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: shell.title,
    description: shell.description,
    url: shell.canonicalUrl,
    image: shell.image,
    about: {
      '@type': 'Thing',
      name: shell.displayName,
    },
    keywords: [shell.displayName, shell.type, 'PixAI'].join(', '),
  }
}

export function TagPageShell({ shell }: { shell: TagPageShellData }) {
  return (
    <section
      data-track-1={`tag-page-shell:${shell.type}`}
      className="flex flex-col gap-4 py-4 msm:py-6 mlg:py-8"
      suppressHydrationWarning
    >
      <script
        type="application/ld+json"
        suppressHydrationWarning
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(buildTagJsonLd(shell)),
        }}
      />

      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2 text-xs text-secondary">
          <span className="inline-flex rounded-full bg-background-secondary px-3 py-1 font-medium uppercase tracking-wide">
            {shell.type}
          </span>
          <span className="inline-flex rounded-full border border-black/10 px-3 py-1 dark:border-white/10">
            {shell.displayName}
          </span>
        </div>

        <h1
          className="text-2xl font-semibold mmd:text-3xl"
          suppressHydrationWarning
        >
          {shell.heroTitle}
        </h1>
        <p
          className="max-w-3xl text-sm text-primary/80 mmd:text-base"
          suppressHydrationWarning
        >
          {shell.heroDescription}
        </p>
      </header>

      <div className="grid gap-4 mlg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
        <aside className="rounded-2xl border border-black/10 p-4 dark:border-white/10">
          {shell.image ? (
            <img
              src={shell.image}
              alt={shell.displayName}
              className="aspect-square w-full rounded-xl object-cover"
              loading="eager"
            />
          ) : (
            <div
              className="aspect-square w-full rounded-xl bg-background-secondary"
              aria-hidden
            />
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex rounded-full bg-background-secondary px-3 py-1 text-xs">
              {shell.displayName}
            </span>
            <span
              className="inline-flex rounded-full border border-black/10 px-3 py-1 text-xs dark:border-white/10"
              aria-hidden
            >
              ...
            </span>
            <span
              className="inline-flex rounded-full border border-black/10 px-3 py-1 text-xs dark:border-white/10"
              aria-hidden
            >
              ...
            </span>
          </div>
        </aside>

        <section className="rounded-2xl border border-black/10 p-4 dark:border-white/10">
          <div className="h-4 w-40 rounded-full bg-black/10 dark:bg-white/10" />
          <div
            className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3"
            aria-hidden
          >
            {Array.from({ length: 6 }, (_, index) => (
              <article
                key={index}
                className="overflow-hidden rounded-2xl bg-background-secondary"
              >
                <div className="aspect-square bg-black/5 dark:bg-white/5" />
                <div className="space-y-3 p-4">
                  <div className="h-4 w-2/3 rounded-full bg-black/10 dark:bg-white/10" />
                  <div className="h-3 w-full rounded-full bg-black/10 dark:bg-white/10" />
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </section>
  )
}

export type { TagPageShellData }
